/**
 * `Login` — pagina pubblica di avvio del flusso OIDC (Authorization Code + PKCE).
 *
 * Design ref: "Auth flow OIDC" / "Routing e guardie per ruolo": `/login` è una
 * rotta pubblica (fuori da `ProtectedRoute`) che avvia il redirect di login.
 *
 * Comportamento:
 *  - se lo stato di autenticazione è ancora in caricamento, mostra un messaggio
 *    di attesa;
 *  - se la sessione è già valida, reindirizza alla home (`/`, hub di scelta
 *    della sezione);
 *  - altrimenti mostra il brand (fuori dalla card), il titolo "Accedi", una
 *    breve descrizione del portale e il pulsante di accesso che avvia `login()`
 *    (OIDC `signinRedirect`) solo su interazione dell'utente.
 *
 * Nota di robustezza: `/login` NON avvia `signinRedirect` automaticamente al
 * mount. L'avvio del redirect è un'azione esplicita dell'utente (click sul
 * pulsante): questo garantisce che sia impossibile innescare un loop di
 * redirect qualora la sessione non si stabilisca al ritorno dall'IdP.
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button, Loading } from '../components/ui';

export default function Login() {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <div className="auth-layout">
        <span className="navbar__brand auth-layout__brand">
          <img className="navbar__logo" src="/imgs/logo.png" alt="Skills" />
        </span>
        <section aria-label="Login" className="auth-status">
          <Loading>Verifica della sessione…</Loading>
        </section>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onSignIn = () => {
    void login().catch((err) => {
      // In caso di errore l'utente resta sulla pagina; l'errore è tracciato.
      console.error('Login: avvio del flusso OIDC non riuscito', err);
    });
  };

  return (
    <div className="auth-layout">
      <span className="navbar__brand auth-layout__brand">
        <img className="navbar__logo" src="/imgs/logo.png" alt="Skills" />
      </span>
      <section aria-labelledby="login-title" className="auth-status">
        <h1 id="login-title">Accedi</h1>
        <p className="auth-status__lead">
          Portale di autovalutazione delle competenze: accedi con il tuo account
          aziendale per compilare e consultare le tue assessment.
        </p>
        <Button type="button" variant="primary" block onClick={onSignIn}>
          Accedi con SSO
        </Button>
      </section>
    </div>
  );
}
